import { useEffect, useMemo, useState } from 'react';

interface UseLoadMoreProps<T> {
  data: T[];
  pageSize: number;
}

interface UseLoadMoreResult<T> {
  visibleData: T[];
  hasMore: boolean;
  loadMore: () => void;
}

/** Progressive "load more" pagination – reveals `pageSize` more items on each call. */
export function useLoadMore<T>({
  data,
  pageSize,
}: UseLoadMoreProps<T>): UseLoadMoreResult<T> {
  const [visibleCount, setVisibleCount] = useState(pageSize);

  // Reset when the source data changes (e.g. filters applied)
  useEffect(() => {
    setVisibleCount(pageSize);
  }, [data.length, pageSize]);

  const visibleData = useMemo(
    () => data.slice(0, visibleCount),
    [data, visibleCount],
  );

  const hasMore = data.length > visibleCount;

  const loadMore = () => setVisibleCount((prev) => prev + pageSize);

  return { visibleData, hasMore, loadMore };
}
